import React, { useCallback, useState } from "react";
import { COLORS, FONT_BODY, FONT_IMPORT } from "./theme.js";
import { isSupabaseConfigured } from "./lib/supabase.js";
import { AuthProvider, useAuth } from "./auth/AuthProvider.jsx";
import { ToastProvider } from "./components/Toast.jsx";
import SplashScreen from "./screens/SplashScreen.jsx";
import LoginScreen from "./screens/LoginScreen.jsx";
import SetupNotice from "./screens/SetupNotice.jsx";
import SetNewPassword from "./screens/SetNewPassword.jsx";
import AppShell from "./AppShell.jsx";

/* Height is set in CSS rather than here because it needs two declarations, and
   an inline style object can only hold one.

   `100vh` on iOS Safari is the viewport with the browser chrome retracted, not
   the one you can actually see. The shell is `overflow: hidden` and scrolls
   internally, so the page never scrolls to reveal the difference — the bottom
   ~80px simply sits behind Safari's toolbar forever. That is what was eating
   the toasts, and it clips the bottom nav too. `100dvh` is the visible
   viewport; the `100vh` line stays as the fallback for anything that predates
   it. */
const SHELL_HEIGHT_CSS = `
  .senogara-viewport, .senogara-shell { height: 100vh; height: 100dvh; }
`;

const shellStyle = {
  width: "100%",
  maxWidth: 420,
  margin: "0 auto",
  background: COLORS.black,
  color: COLORS.cream,
  fontFamily: FONT_BODY,
  display: "flex",
  flexDirection: "column",
  position: "relative",
  overflow: "hidden",
};

function Routes() {
  const { session, loading, recovering } = useAuth();
  const [splashDone, setSplashDone] = useState(false);
  // Stable callback so the splash timer isn't restarted on every render.
  const finishSplash = useCallback(() => setSplashDone(true), []);

  if (!isSupabaseConfigured) return <SetupNotice />;

  // Hold the splash until the stored session has been restored, so a returning
  // user never sees the login screen flash before landing in their Passport.
  if (!splashDone || loading) return <SplashScreen onContinue={finishSplash} />;

  if (!session) return <LoginScreen />;

  /* Has to come before AppShell, and that is the entire fix: a reset link
     produces a genuine session, so `session` alone is true here and the app
     would otherwise open normally — signed in, with the password still the
     forgotten one. An expired link produces no session at all, which the line
     above already handles by showing the sign-in screen. */
  if (recovering) return <SetNewPassword />;

  return <AppShell />;
}

export default function App() {
  return (
    <div className="senogara-viewport" style={{ background: "#050505", display: "flex", justifyContent: "center" }}>
      <style>{FONT_IMPORT}</style>
      <style>{SHELL_HEIGHT_CSS}</style>
      <div className="senogara-shell" style={shellStyle}>
        {/* ToastProvider renders its toast inside the shell, which is
            position: relative — that's what the toast anchors to. */}
        <AuthProvider>
          <ToastProvider>
            <Routes />
          </ToastProvider>
        </AuthProvider>
      </div>
    </div>
  );
}
